import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import api from '../lib/apiClient';
import { getSilentLocation, reverseGeocodeLatLng } from '../utils/location';

const CartContext = createContext(null);

// The cart/address flow is customer-only. On cook/admin routes we must never make
// customer API calls (apiClient would attach the cook/admin token, which /auth/*
// rejects) — so we skip them entirely.
const isCustomerApp = () => {
    const p = (typeof window !== 'undefined' && window.location.pathname) || '';
    return !p.startsWith('/cook') && !p.startsWith('/admin');
};

export const CartProvider = ({ children }) => {
    const { token } = useAuth();
    const isHydrated = useRef(false);
    
    const [cart, setCart] = useState(() => {
        try {
            const saved = localStorage.getItem('cart');
            const parsed = saved ? JSON.parse(saved) : [];
            isHydrated.current = true;
            return parsed;
        } catch {
            isHydrated.current = true;
            return [];
        }
    });
    
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
    // ---- Edit-an-existing-booking mode (Add Dishes flow for on-demand bookings) ----
    // When set, the Home + review screens operate on this booking; the live cart is
    // stashed and restored on exit, and the "Additional Payment" is the order-edit delta.
    const [editBooking, setEditBooking] = useState(null);
    const [editDelta, setEditDelta] = useState(0);
    const [editQuote, setEditQuote] = useState(null); // full order-edit quote (old_total, discounts, etc.)
    const [editQuoting, setEditQuoting] = useState(false);
    // Multi-booking "session edit mode": pick dishes for one not-yet-paid MB session.
    // Parallel to editBooking, but there's no payment/delta — the cart Total is shown.
    const [editSession, setEditSession] = useState(null); // { key, dateStr, slot, label }
    const cartSnapshotRef = useRef(null);
    const [cartReady, setCartReady] = useState(false);
    // Tiered multi-dish discount %s [rank0, rank1(2nd), rank2(3rd), rank3+(4th+)],
    // loaded from admin config so display matches the backend-authoritative charge.
    const [tierPcts, setTierPcts] = useState([0, 0, 0, 0]);
    // Portion pricing config (global). maxPortion caps the people selector; portionPricing
    // holds the linear (per-portion %) + batch (ranges) tables used to compute multipliers.
    const [maxPortion, setMaxPortion] = useState(6);
    const [portionPricing, setPortionPricing] = useState(null);
    // Resolved once we know the account's address (from DB for logged-in users,
    // or immediately from localStorage for guests). Gates the blocking prompt.
    const [addressReady, setAddressReady] = useState(false);
    // True while we're silently resolving serviceability from GPS (prevents the Home
    // fallback sheet from flashing open during a slow GPS fix when permission is granted).
    const [locating, setLocating] = useState(false);
    
    // Single user address (simplified)
    const [userAddress, setUserAddress] = useState(() => {
        try {
            const saved = localStorage.getItem('userAddress');
            return saved ? JSON.parse(saved) : null;
        } catch {
            return null;
        }
    });
    
    // Location status - persisted ONLY when a complete delivery address is saved.
    // With no complete address, serviceability is re-resolved every session (not trusted
    // from the previous visit), so we start "unchecked" instead of restoring it.
    const [locationStatus, setLocationStatus] = useState(() => {
        const UNCHECKED = {
            checking: false, checked: false, serviceable: null,
            distance: null, message: null, error: null, coords: null,
        };
        try {
            const savedAddress = JSON.parse(localStorage.getItem('userAddress') || 'null');
            const complete = !!(savedAddress && (savedAddress.flatNo || '').trim() && typeof savedAddress.lat === 'number');
            if (!complete) {
                // No complete address — hydrate a previously pinned (temp) location if we have
                // one so the area selector doesn't re-prompt on reopen.
                const pinned = JSON.parse(localStorage.getItem('tossit_pinned_location') || 'null');
                if (pinned && typeof pinned.lat === 'number') {
                    return {
                        checking: false, checked: true,
                        serviceable: pinned.serviceable ?? null, distance: pinned.distance ?? null,
                        message: null, error: null,
                        coords: { latitude: pinned.lat, longitude: pinned.lng, title: pinned.title, subtitle: pinned.subtitle },
                    };
                }
                return UNCHECKED;
            }
            const savedStatus = localStorage.getItem('locationStatus');
            if (savedStatus) return JSON.parse(savedStatus);
            return {
                checking: false,
                checked: true,
                serviceable: savedAddress.serviceable ?? true,
                distance: savedAddress.distance ?? null,
                message: null,
                error: null,
                coords: { latitude: savedAddress.lat, longitude: savedAddress.lng },
            };
        } catch {
            return UNCHECKED;
        }
    });

    useEffect(() => {
        setCartReady(true);
    }, []);

    useEffect(() => {
        if (editBooking || editSession) return; // don't persist the transient edit cart over the real one
        localStorage.setItem('cart', JSON.stringify(cart));
    }, [cart, editBooking, editSession]);

    // Begin editing a booking: stash the live cart, seed with the booking's dishes.
    const beginEditBooking = (booking, seededItems) => {
        cartSnapshotRef.current = cart;
        setCart(seededItems || []);
        setEditDelta(0);
        setEditBooking(booking);
    };
    // Exit edit mode (cancel or after a successful edit): restore the stashed cart.
    const exitEditBooking = () => {
        setEditBooking(null);
        setEditDelta(0);
        setEditQuote(null);
        setCart(cartSnapshotRef.current || []);
        cartSnapshotRef.current = null;
    };

    // Begin editing an MB session's dishes: stash the live cart, seed with the session's picks.
    const beginEditSession = (session, seededItems) => {
        cartSnapshotRef.current = cart;
        setCart(seededItems || []);
        setEditSession(session);
    };
    // Exit session edit mode: restore the stashed cart.
    const exitEditSession = () => {
        setEditSession(null);
        setCart(cartSnapshotRef.current || []);
        cartSnapshotRef.current = null;
    };

    // Commit the current cart as the dishes for the MB session being edited, WITHOUT the
    // intermediate review screen (used by the "Add dishes from MB preview" flow). Prices the
    // cart, writes the picks + price into the mbSessionEdit store (read back by the preview),
    // then exits edit mode. Returns the preview path to navigate to, or null on failure.
    const commitSessionDishes = async () => {
        if (!editSession || cart.length === 0) return null;
        const addonIdsOf = (addons) => (addons || []).map(a => a.addon_id || a.id).filter(Boolean);
        const items = cart.map(i => ({ dish_id: i.id, quantity: i.quantity, addon_ids: addonIdsOf(i.addons) }));
        const res = await api.post('/payments/quote-cart', { items });
        const price = Math.round(res.data?.total || 0);
        const picks = cart.map(i => ({ key: i.id, dish: i, portions: i.quantity, addons: i.addons || [] }));
        let store = null;
        try { store = JSON.parse(sessionStorage.getItem('mbSessionEdit') || 'null'); } catch { store = null; }
        if (!store) store = { sig: '', picks: {}, prices: {} };
        store.picks = { ...(store.picks || {}), [editSession.key]: picks };
        store.prices = { ...(store.prices || {}), [editSession.key]: price };
        sessionStorage.setItem('mbSessionEdit', JSON.stringify(store));
        const returnTo = editSession.returnTo || '/multi-booking/review';
        exitEditSession();
        return returnTo;
    };

    // While editing, live-quote the order-edit delta (Additional Payment) as the cart changes.
    useEffect(() => {
        if (!editBooking) return;
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                setEditQuoting(true);
                const items = cart.map(i => ({
                    dish_id: i.id,
                    quantity: i.quantity,
                    addon_ids: (i.addons || []).map(a => a.addon_id || a.id).filter(Boolean),
                }));
                const res = await api.post('/payments/quote-order-edit', { booking_id: editBooking.id, items });
                if (!cancelled) {
                    setEditDelta(Math.max(0, Math.round(res.data?.delta || 0)));
                    setEditQuote(res.data || null);
                }
            } catch {
                if (!cancelled) { setEditDelta(0); setEditQuote(null); }
            } finally {
                if (!cancelled) setEditQuoting(false);
            }
        }, 350);
        return () => { cancelled = true; clearTimeout(t); };
    }, [editBooking, cart]);

    // Persist user address
    useEffect(() => {
        if (userAddress) {
            localStorage.setItem('userAddress', JSON.stringify(userAddress));
        } else {
            localStorage.removeItem('userAddress');
        }
    }, [userAddress]);

    // A "complete" delivery address = has a flat/building and coordinates. Serviceability-only
    // locations (coords picked at signup) are NOT complete until flat/building is added at checkout.
    const hasCompleteAddress = !!(userAddress && (userAddress.flatNo || '').trim() && typeof userAddress.lat === 'number');

    // Persist location status only when a complete address is saved; otherwise drop any
    // stale persisted serviceability so the next session re-checks from scratch.
    useEffect(() => {
        if (hasCompleteAddress && locationStatus.checked) {
            localStorage.setItem('locationStatus', JSON.stringify(locationStatus));
        } else if (!hasCompleteAddress) {
            localStorage.removeItem('locationStatus');
        }
    }, [locationStatus, hasCompleteAddress]);

    const checkLocationServiceability = useCallback(async (lat, lng) => {
        try {
            const response = await api.post('/check-location', { latitude: lat, longitude: lng });
            return response.data;
        } catch (error) {
            console.error('Service area check failed:', error);
            return { serviceable: false };
        }
    }, []);

    // Persist a pin-only "temp" location so reopening the app doesn't re-prompt the area
    // selector. Stored locally for everyone + on the account for logged-in users (cross-device).
    // A full delivery address (saveUserAddress) supersedes and clears this.
    const persistPinnedLocation = useCallback((lat, lng, serviceable, meta = {}) => {
        // Once a full delivery address exists it is the source of truth (incl. the admin
        // dashboard) — never create/sync a temp pin, and proactively clear any stale one.
        if (hasCompleteAddress) {
            try { localStorage.removeItem('tossit_pinned_location'); } catch { /* ignore */ }
            return;
        }
        const pin = { lat, lng, serviceable: serviceable ?? null, title: meta.title || null, subtitle: meta.subtitle || null };
        try { localStorage.setItem('tossit_pinned_location', JSON.stringify(pin)); } catch { /* ignore */ }
        if (token) api.put('/auth/pinned-location', pin).catch(() => { /* keep local copy */ });
    }, [token, hasCompleteAddress]);

    // Set serviceability from coordinates alone (no flat/building). Used post-signup to
    // decide "do we serve here" without collecting a full delivery address.
    const resolveServiceabilityFromCoords = useCallback(async (lat, lng, meta = {}) => {
        setLocationStatus(prev => ({ ...prev, checking: true }));
        try {
            const res = await checkLocationServiceability(lat, lng);
            setLocationStatus({
                checking: false,
                checked: true,
                serviceable: res?.serviceable ?? false,
                distance: res?.distance_km ?? null,
                message: null,
                error: null,
                coords: { latitude: lat, longitude: lng, title: meta.title, subtitle: meta.subtitle },
            });
            persistPinnedLocation(lat, lng, res?.serviceable ?? false, meta);
            return res;
        } catch {
            setLocationStatus(prev => ({
                ...prev, checking: false, checked: true, serviceable: false,
                coords: { latitude: lat, longitude: lng, title: meta.title, subtitle: meta.subtitle },
            }));
            persistPinnedLocation(lat, lng, false, meta);
            return { serviceable: false };
        }
    }, [checkLocationServiceability, persistPinnedLocation]);

    // Attempt to resolve serviceability silently from current GPS (only when permission is
    // already granted — never prompts). Reverse-geocodes so the saved location has a readable
    // title/subtitle instead of raw coordinates. Returns true if it resolved, false otherwise.
    const runSilentServiceability = useCallback(async () => {
        setLocating(true);
        try {
            const loc = await getSilentLocation();
            if (!loc || typeof loc.lat !== 'number') return false;
            let title, subtitle;
            if (loc.address) {
                title = loc.address.name || loc.address.street || 'Current location';
                subtitle = loc.address.formatted;
            } else {
                const rg = await reverseGeocodeLatLng(loc.lat, loc.lng);
                if (rg) { title = rg.title; subtitle = rg.subtitle; }
            }
            await resolveServiceabilityFromCoords(loc.lat, loc.lng, { title, subtitle });
            return true;
        } finally {
            setLocating(false);
        }
    }, [resolveServiceabilityFromCoords]);

    // Save/update user address (also persists to the account when logged in)
    const saveUserAddress = (addressData) => {
        setUserAddress(addressData);
        
        // Update location status based on address
        setLocationStatus(prev => ({
            ...prev,
            checked: true,
            serviceable: addressData.serviceable,
            distance: addressData.distance,
            coords: { latitude: addressData.lat, longitude: addressData.lng }
        }));

        // Persist to the user's account so it syncs across devices
        if (token) {
            api.put('/auth/address', addressData).catch(() => { /* keep local copy even if the sync fails */ });
        }
        // A full delivery address supersedes any temp pin — drop it locally (the account copy
        // is cleared server-side by the address save above).
        if ((addressData.flatNo || '').trim()) {
            try { localStorage.removeItem('tossit_pinned_location'); } catch { /* ignore */ }
        }
    };

    // On login, the account's address is the source of truth. Load it into the app.
    // Guest → resolve immediately from localStorage. Logged-in with no saved address
    // but a local (guest) one → push the local address up to the account.
    useEffect(() => {
        let active = true;
        const load = async () => {
            if (!token || !isCustomerApp()) { if (active) setAddressReady(true); return; }
            try {
                const res = await api.get('/auth/address');
                const data = res.data;
                if (!active) return;
                if (data && data.address && typeof data.address.lat === 'number') {
                    const addr = data.address;
                    setUserAddress(addr);
                    setLocationStatus(prev => ({
                        ...prev,
                        checked: true,
                        serviceable: addr.serviceable ?? null,
                        distance: addr.distance ?? null,
                        coords: { latitude: addr.lat, longitude: addr.lng }
                    }));
                } else {
                    // Account has no complete address. Migrate a guest's complete local address,
                    // else reconcile the temp pinned location so we don't re-prompt on reopen.
                    let local = null;
                    try { local = JSON.parse(localStorage.getItem('userAddress') || 'null'); } catch { local = null; }
                    if (local && typeof local.lat === 'number' && (local.flatNo || '').trim()) {
                        api.put('/auth/address', local).catch(() => {});
                    } else {
                        setUserAddress(null);
                        try {
                            const pres = await api.get('/auth/pinned-location');
                            if (!active) return;
                            const pin = pres.data?.pinned_location;
                            let localPin = null;
                            try { localPin = JSON.parse(localStorage.getItem('tossit_pinned_location') || 'null'); } catch { localPin = null; }
                            if (pin && typeof pin.lat === 'number') {
                                // Account pin is the source of truth across devices.
                                try { localStorage.setItem('tossit_pinned_location', JSON.stringify(pin)); } catch { /* ignore */ }
                                setLocationStatus(prev => prev.checked ? prev : ({
                                    checking: false, checked: true,
                                    serviceable: pin.serviceable ?? null, distance: null,
                                    message: null, error: null,
                                    coords: { latitude: pin.lat, longitude: pin.lng, title: pin.title, subtitle: pin.subtitle },
                                }));
                            } else if (localPin && typeof localPin.lat === 'number') {
                                // Push the guest's local pin up to the freshly logged-in account.
                                api.put('/auth/pinned-location', localPin).catch(() => {});
                            }
                        } catch { /* no pinned location — ignore */ }
                    }
                }
            } catch { /* network issue — fall back to whatever localStorage had */ }
            if (active) setAddressReady(true);
        };
        setAddressReady(false);
        load();
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Clear user address
    const clearUserAddress = () => {
        setUserAddress(null);
        setLocationStatus({
            checking: false,
            checked: false,
            serviceable: null,
            distance: null,
            message: null,
            error: null,
            coords: null
        });
        localStorage.removeItem('locationStatus');
    };

    // Portion price multiplier for a pricing type ('batch' | 'linear'), read from config.
    const getPortionMultiplier = (portions, ptype) => {
        const mp = maxPortion || 6;
        const p = Math.max(1, Math.min(Number(portions) || 1, mp));
        const pp = portionPricing;
        if (!pp) { // fallback = the legacy batch curve
            if (p <= 3) return 1;
            if (p <= 5) return 1.5;
            return 2;
        }
        if ((ptype || 'batch') === 'linear') {
            if (p <= 1) return 1;
            const pct = Number((pp.linear || {})[p] ?? (pp.linear || {})[String(p)] ?? 0);
            return 1 + pct / 100;
        }
        for (const r of (pp.batch || [])) {
            if (p >= Number(r.start) && p <= Number(r.end)) return 1 + Number(r.pct || 0) / 100;
        }
        return 1;
    };

    // Per-component, type-based total: dish scaled by its type + each add-on by its own type.
    const calcItemTotal = (dish, addons, quantity) => {
        const dishMult = getPortionMultiplier(quantity, dish?.pricing_type);
        let total = Math.round((dish?.price || 0) * dishMult);
        for (const a of (addons || [])) {
            total += Math.round((a.price || 0) * getPortionMultiplier(quantity, a.pricing_type));
        }
        return total;
    };

    // Build a normalized cart line (used to seed the edit cart from a booking's dishes).
    const buildCartItem = (dish, quantity = 2, addons = []) => ({
        ...dish, quantity, addons, totalPrice: calcItemTotal(dish, addons, quantity),
    });

    const addToCart = (dish, quantity = 1, addons = []) => {
        setCart(prevCart => {
            const existingIndex = prevCart.findIndex(item => item.id === dish.id);
            const totalPrice = calcItemTotal(dish, addons, quantity);

            if (existingIndex >= 0) {
                const newCart = [...prevCart];
                newCart[existingIndex] = {
                    ...newCart[existingIndex],
                    ...dish,
                    quantity,
                    addons,
                    totalPrice
                };
                return newCart;
            }
            return [...prevCart, { ...dish, quantity, addons, totalPrice }];
        });
    };

    const removeFromCart = (dishId) => {
        setCart(prevCart => prevCart.filter(item => item.id !== dishId));
    };

    const updateQuantity = (dishId, quantity) => {
        if (quantity < 1) {
            removeFromCart(dishId);
            return;
        }
        setCart(prevCart => 
            prevCart.map(item => {
                if (item.id === dishId) {
                    const totalPrice = calcItemTotal(item.price, item.addons, quantity);
                    return { ...item, quantity, totalPrice };
                }
                return item;
            })
        );
    };

    const clearCart = () => {
        setCart([]);
        setSelectedTimeSlot(null);
    };

    // Load the configured multi-dish discount tiers + portion pricing (customer app, signed in).
    useEffect(() => {
        if (!token || !isCustomerApp()) return;
        let cancelled = false;
        api.get('/app/config')
            .then((res) => {
                if (cancelled) return;
                const t = res.data?.dish_tier_discounts;
                if (t) {
                    setTierPcts([0, Number(t.second) || 0, Number(t.third) || 0, Number(t.fourth_plus) || 0]);
                }
                if (res.data?.max_portion) setMaxPortion(Number(res.data.max_portion) || 6);
                if (res.data?.portion_pricing) setPortionPricing(res.data.portion_pricing);
            })
            .catch(() => { /* keep defaults for guests / offline */ });
        return () => { cancelled = true; };
    }, [token]);

    // Portion price multiplier for a given pricing type ('batch' | 'linear'), read from config.
    const getCartTotal = () => {
        return cart.reduce((total, item) => total + (item.totalPrice || item.price * item.quantity), 0);
    };

    // Tiered multi-dish discount: sort by effective price (highest first);
    // percentages by rank come from admin config (default 0/20/30/40).
    const getDiscountPct = (rank) => tierPcts[Math.min(rank, tierPcts.length - 1)] || 0;

    const getCartPricing = () => computePricing(cart);

    // Same ranked tier-discount logic as the cart, for an arbitrary item list (e.g. an MB session).
    const computePricing = (items) => {
        const ranked = [...(items || [])]
            .map(item => ({ item, effective: item.totalPrice || item.price * item.quantity }))
            .sort((a, b) => b.effective - a.effective);

        let originalSubtotal = 0;
        let discountedSubtotal = 0;
        const lines = ranked.map(({ item, effective }, idx) => {
            const discountPct = getDiscountPct(idx);
            const discountedPrice = Math.round(effective * (1 - discountPct / 100));
            originalSubtotal += effective;
            discountedSubtotal += discountedPrice;
            return { ...item, effective, originalPrice: effective, discountPct, discountedPrice };
        });

        return {
            lines,
            originalSubtotal,
            discountedSubtotal,
            tierDiscount: originalSubtotal - discountedSubtotal,
        };
    };

    const getCartCount = () => {
        return cart.length;
    };

    return (
        <CartContext.Provider value={{
            cart,
            cartReady,
            addressReady,
            selectedTimeSlot,
            editBooking,
            editDelta,
            editQuote,
            editQuoting,
            editSession,
            beginEditSession,
            exitEditSession,
            commitSessionDishes,
            beginEditBooking,
            exitEditBooking,
            buildCartItem,
            locationStatus,
            userAddress,
            hasCompleteAddress,
            locating,
            runSilentServiceability,
            setSelectedTimeSlot,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            getCartTotal,
            getCartPricing,
            computePricing,
            getCartCount,
            getPortionMultiplier,
            maxPortion,
            saveUserAddress,
            clearUserAddress,
            checkLocationServiceability,
            resolveServiceabilityFromCoords
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
